/**
 * Ravintola-moduulin OMA kirjautumissivu (Phase 20)
 *
 * Käyttää /api/restaurant-staff/login (email + password) tai
 * /api/restaurant-staff/pin-login (PIN + email).
 * Tunnukset ovat täysin erillisiä Apps2Go-tunnuksista.
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Utensils, KeyRound, Mail, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAFF_TOKEN_KEY = "apps2go.restaurant_staff_token";
const STAFF_USER_KEY = "apps2go.restaurant_staff_user";

export function setRestaurantStaffSession(token, staff) {
  localStorage.setItem(STAFF_TOKEN_KEY, token);
  localStorage.setItem(STAFF_USER_KEY, JSON.stringify(staff));
}
export function getRestaurantStaffToken() { return localStorage.getItem(STAFF_TOKEN_KEY); }
export function getRestaurantStaffUser() {
  try { return JSON.parse(localStorage.getItem(STAFF_USER_KEY) || "null"); } catch { return null; }
}
export function clearRestaurantStaffSession() {
  localStorage.removeItem(STAFF_TOKEN_KEY);
  localStorage.removeItem(STAFF_USER_KEY);
}

const ROLE_PATH = {
  waiter: "/restaurant/waiter",
  cook: "/restaurant/kitchen",
  cashier: "/restaurant/cashier",
  manager: "/restaurant",
  host: "/restaurant/waiter",
};

export default function RestaurantStaffLogin() {
  const [mode, setMode] = useState("password"); // "password" | "pin"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email) return toast.error("Syötä sähköposti");
    if (mode === "password" && !password) return toast.error("Syötä salasana");
    if (mode === "pin" && (!pin || pin.length !== 4)) return toast.error("Syötä 4-numeroinen PIN");
    setLoading(true);
    try {
      const url = mode === "pin" ? "/restaurant-staff/pin-login" : "/restaurant-staff/login";
      const body = mode === "pin" ? { pin, email } : { email, password };
      const r = await axios.post(`${API}${url}`, body);
      setRestaurantStaffSession(r.data.token, r.data.staff);
      toast.success(`Tervetuloa, ${r.data.staff.name}!`);
      const path = ROLE_PATH[r.data.staff.role] || "/restaurant/waiter";
      // Käytä full-reloadia jotta auth context resetoituu puhtaasti
      window.location.href = path;
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Kirjautuminen epäonnistui");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen app-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 flex items-center justify-center shadow-2xl shadow-amber-500/30">
            <Utensils className="w-8 h-8 text-white" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mt-3">Ravintolan henkilökunta</p>
          <h1 className="text-2xl font-display gradient-text-luxe mt-1">Kirjaudu sisään</h1>
          <p className="text-xs text-white/40 mt-1">Tämä on ravintolamoduulin oma kirjautuminen.<br/>Apps2Go-pääkirjautumiseen siirry <a href="/login" className="text-amber-300 hover:underline">tästä</a>.</p>
        </div>

        <div className="glass-card p-6 space-y-4">
          {/* Mode-switch */}
          <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-white/[0.04] border border-white/5">
            <button
              type="button"
              onClick={() => setMode("password")}
              className={`py-2 px-3 rounded-md text-xs transition flex items-center justify-center gap-1.5 ${mode === "password" ? "bg-amber-500/15 text-amber-200" : "text-white/50 hover:text-white"}`}
              data-testid="restaurant-staff-login-mode-password"
            >
              <Mail className="w-3.5 h-3.5" /> Salasana
            </button>
            <button
              type="button"
              onClick={() => setMode("pin")}
              className={`py-2 px-3 rounded-md text-xs transition flex items-center justify-center gap-1.5 ${mode === "pin" ? "bg-amber-500/15 text-amber-200" : "text-white/50 hover:text-white"}`}
              data-testid="restaurant-staff-login-mode-pin"
            >
              <KeyRound className="w-3.5 h-3.5" /> PIN-koodi
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs text-white/60">Sähköposti</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="etunimi@ravintola.fi"
                autoComplete="email"
                data-testid="restaurant-staff-login-email"
              />
            </div>

            {mode === "password" ? (
              <div>
                <Label className="text-xs text-white/60">Salasana</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="restaurant-staff-login-password"
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs text-white/60">PIN-koodi (4 numeroa)</Label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="restaurant-staff-login-pin"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-zinc-900 hover:from-amber-400 hover:to-orange-500 font-semibold"
              data-testid="restaurant-staff-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><span>Kirjaudu</span> <ArrowRight className="w-4 h-4 ml-1" /></>)}
            </Button>
          </form>
        </div>

        <p className="text-center text-[10px] text-white/30 mt-4">
          Ravintolan henkilökunta-rekisteri on TÄYSIN itsenäinen<br/>
          eikä se ole linkitetty Apps2Go-pääkäyttäjähallintaan.
        </p>
      </div>
    </div>
  );
}
